import { useRef, useState, useEffect, useCallback, useMemo, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiContextProvidedValue, BiData, } from '@wix/da-bi/pkg/events';
import { subscribeToTier } from '../../Tier/redux/consumer/actions';
import { isConsumerReduxReady, isMatureFilterOverridden, } from '../../Tier/redux/consumer/selectors';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import { DeviationResourceType } from '../../types/deviation';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { formatPrice } from '../../DeviationViews/TierProductCardDumb/utils';
export function useSubscribeButton(tier, _a) {
    var _b = _a === void 0 ? {} : _a, type = _b.type, matureFilterDisabled = _b.matureFilterDisabled, referral_info = _b.referral_info, showUnsubAsView = _b.showUnsubAsView, withPrice = _b.withPrice, hrefProp = _b.href;
    var waitingAction = useRef();
    var _c = useState(false), loadRedux = _c[0], setLoadRedux = _c[1];
    var t = useTranslation().t;
    var showBlocked = useDeviationMatureFilter(tier).showBlocked;
    var reduxReady = useSelector(isConsumerReduxReady);
    var matureFilterOverridden = useSelector(function (state) {
        return isMatureFilterOverridden(state, tier === null || tier === void 0 ? void 0 : tier.deviationId);
    });
    var dispatch = useDispatch();
    var restrictToLoggedIn = useRestrictToLoggedIn().restrictToLoggedIn;
    var isMatureBlocked = showBlocked && !matureFilterDisabled && !matureFilterOverridden;
    useEffect(function () {
        if (reduxReady && waitingAction.current) {
            waitingAction.current();
            waitingAction.current = undefined;
        }
    }, [reduxReady]);
    useEffect(function () {
        if (isMatureBlocked) {
            // mature blocked shows disabled, so we cannot wait for a click
            // to load the redux stuff
            setLoadRedux(true);
        }
    }, [isMatureBlocked]);
    var variant = useMemo(function () {
        var _a, _b;
        if (!tier) {
            return 'disabled';
        }
        if (type) {
            return type;
        }
        if ((_a = tier === null || tier === void 0 ? void 0 : tier.tier) === null || _a === void 0 ? void 0 : _a.isUserSubscribed) {
            if (showUnsubAsView) {
                return 'view';
            }
            return 'subscribed';
        }
        if (isMatureBlocked || !((_b = tier === null || tier === void 0 ? void 0 : tier.tier) === null || _b === void 0 ? void 0 : _b.canUserSubscribe)) {
            return 'disabled';
        }
        return 'subscribe';
    }, [isMatureBlocked, showUnsubAsView, tier, type]);
    var subscribe = useCallback(function (e) {
        if (!tier) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        if (!reduxReady) {
            waitingAction.current = function () {
                dispatch(subscribeToTier(tier));
            };
            setLoadRedux(true);
            return;
        }
        dispatch(subscribeToTier(tier));
    }, [dispatch, reduxReady, tier]);
    var _d = useMemo(function () {
        var _a, _b;
        switch (variant) {
            case 'subscribe':
                return {
                    onClick: restrictToLoggedIn(ReferralType.MEMBER_SUBSCRIBE, subscribe),
                    href: hrefProp,
                    biData: BiData({
                        evid: 550,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: referral_info !== null && referral_info !== void 0 ? referral_info : BiContextProvidedValue,
                    }),
                    text: withPrice && ((_a = tier === null || tier === void 0 ? void 0 : tier.tier) === null || _a === void 0 ? void 0 : _a.dollarPrice)
                        ? t('deviation.tier.subscribeWithPrice', {
                            dollarPrice: formatPrice((_b = tier === null || tier === void 0 ? void 0 : tier.tier) === null || _b === void 0 ? void 0 : _b.dollarPrice),
                        })
                        : t('deviation.tier.subscribe'),
                };
            case 'view':
                return {
                    href: Url.userSubscriptionsPath({
                        username: tier.author.username,
                        tierDeviationId: tier === null || tier === void 0 ? void 0 : tier.deviationId,
                        tierContentType: 'deviations',
                    }),
                    biData: BiData({
                        evid: 544,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                    }),
                    text: t('deviation.tier.viewSubscription'),
                };
            case 'subscribed':
                return {
                    href: hrefProp,
                    biData: BiData({
                        evid: 551,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: (typeof window !== 'undefined' &&
                            window.location.href === URLS.ORDERS
                            ? ReferralType.BILLING_PAYMENTS
                            : ReferralType.TIER_CARD) || ReferralType.TIER_CARD,
                    }),
                    text: t('deviation.tier.subscribed'),
                };
            case 'disabled':
                return {
                    text: t('deviation.tier.noSubscribing'),
                    disabled: true,
                };
            default:
                return {};
        }
    }, [
        hrefProp,
        referral_info,
        restrictToLoggedIn,
        subscribe,
        t,
        tier,
        variant,
        withPrice,
    ]), onClick = _d.onClick, href = _d.href, biData = _d.biData, text = _d.text, disabled = _d.disabled;
    return { loadRedux: loadRedux, variant: variant, onClick: onClick, href: href, biData: biData, text: text, disabled: disabled };
}
//# sourceMappingURL=useSubscribeButton.js.map